import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { Pencil, Trash2, X, Check, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Comment {
  id: string;
  event_id: string;
  user_id: string;
  content: string;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
  replies?: Comment[];
}

interface EventCommentsProps {
  eventId: string;
  comments: Comment[];
  onCommentUpdate: () => Promise<void>;
}

export function EventComments({ eventId, comments, onCommentUpdate }: EventCommentsProps) {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Group comments by parent_id
  const commentsByParent = comments.reduce((acc, comment) => {
    if (!comment.parent_id) {
      if (!acc.root) acc.root = [];
      acc.root.push(comment);
    } else {
      if (!acc[comment.parent_id]) acc[comment.parent_id] = [];
      acc[comment.parent_id].push(comment);
    }
    return acc;
  }, {} as Record<string, Comment[]>);

  const handleSubmitComment = async (e: React.FormEvent, parentId?: string) => {
    e.preventDefault();
    if (!user || !newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.from('event_comments').insert({
        event_id: eventId,
        user_id: user.id,
        content: newComment.trim(),
        parent_id: parentId || null
      });

      if (error) throw error;

      setNewComment('');
      setReplyTo(null);
      await onCommentUpdate();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!user || submitting) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('event_comments')
        .update({ content: editContent.trim() })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      setEditingCommentId(null);
      setEditContent('');
      await onCommentUpdate();
    } catch (error) {
      console.error('Error editing comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user || submitting) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('event_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      await onCommentUpdate();
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderComment = (comment: Comment, depth = 0) => {
    const isEditing = editingCommentId === comment.id;
    const replies = commentsByParent[comment.id] || [];

    return (
      <div key={comment.id} className={cn("space-y-4", depth > 0 && "ml-8")}>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <img
              src={comment.user.avatar_url}
              alt={comment.user.full_name}
              className="w-8 h-8 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">{comment.user.full_name}</span>
                  <span className="text-sm text-gray-500 ml-2">
                    {new Date(comment.created_at).toLocaleDateString([], {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                {user?.id === comment.user_id && !isEditing && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingCommentId(comment.id);
                        setEditContent(comment.content);
                      }}
                      className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="flex-1 px-3 py-1 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                  <button
                    onClick={() => handleEditComment(comment.id)}
                    disabled={!editContent.trim() || submitting}
                    className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditContent('');
                    }}
                    className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <p className="mt-1 text-gray-800">{comment.content}</p>
                  <button
                    onClick={() => setReplyTo(comment.id)}
                    className="mt-2 text-sm text-gray-500 hover:text-royal-purple flex items-center gap-1"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Reply
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {replyTo === comment.id && (
          <form onSubmit={(e) => handleSubmitComment(e, comment.id)} className="ml-8">
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a reply..."
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
              />
              <button
                type="submit"
                disabled={!newComment.trim() || submitting}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-colors",
                  !newComment.trim() || submitting
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-royal-purple text-white hover:bg-royal-purple/90"
                )}
              >
                {submitting ? 'Posting...' : 'Reply'}
              </button>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {replies.length > 0 && (
          <div className="space-y-4">
            {replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Comment form */}
      <form onSubmit={(e) => handleSubmitComment(e)} className="space-y-4">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          rows={3}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
        />

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className={cn(
              "px-4 py-2 rounded-lg font-medium transition-colors",
              !newComment.trim() || submitting
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-royal-purple text-white hover:bg-royal-purple/90"
            )}
          >
            {submitting ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </form>

      {/* Comments list */}
      <div className="space-y-4">
        {commentsByParent.root?.map(comment => renderComment(comment))}
      </div>
    </div>
  );
}